import React, { useState } from "react";
import { normalizeImgUrl } from "@/lib/utils-img";

/**
 * Imagen con espacio reservado, carga diferida y fundido al aparecer.
 *
 * Tres problemas que arregla de una vez:
 *
 *  1. Ni una sola <img> del frontend público declaraba `width`/`height`, y solo
 *     tres usaban `loading="lazy"`. El envoltorio con `aspect-ratio` reserva el
 *     hueco antes de que la imagen exista, que es lo que evita el salto.
 *  2. La imagen de cabecera del artículo, la de Palabra del Día y la del
 *     archivo ponían `max-height` en el contenedor sin `height`, así que
 *     reservaban espacio cero: el texto entero bajaba de golpe al decodificar.
 *  3. Cuando no había imagen, unas tarjetas pintaban un degradado y otras no
 *     pintaban nada, de modo que en la misma rejilla unas eran más bajas que
 *     otras. Aquí siempre se ocupa el hueco, con `fallback` si se pasa.
 *
 * `priority` marca las imágenes sobre el pliegue: se cargan de inmediato y sin
 * fundido, porque diferirlas retrasaría el contenido principal.
 */
export default function SmartImage({
  src,
  alt = "",
  ratio = "16 / 9",
  className = "",
  imgClassName = "",
  fit = "cover",
  priority = false,
  fallback = null,
  rounded = "",
  ...rest
}) {
  const [loaded, setLoaded] = useState(false);
  const url = src ? normalizeImgUrl(src) : null;

  return (
    <div
      className={`relative overflow-hidden bg-muted ${rounded} ${className}`}
      style={{ aspectRatio: ratio }}
    >
      {url ? (
        <img
          src={url}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          fetchpriority={priority ? "high" : undefined}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          className={`w-full h-full transition-opacity duration-base ease-out-apple ${
            fit === "contain" ? "object-contain" : "object-cover"
          } ${priority || loaded ? "opacity-100" : "opacity-0"} ${imgClassName}`}
          {...rest}
        />
      ) : (
        fallback ?? (
          <div className="w-full h-full flex items-center justify-center bg-[#f5f5f7]">
            <img
              src="/logos/logo-icono.png"
              alt=""
              className="h-10 w-auto opacity-20"
              loading="lazy"
              decoding="async"
            />
          </div>
        )
      )}
    </div>
  );
}
